import { useState, useMemo, useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, AlertModal } from '@components/ui';
import DataTable from '@components/shared/DataTable/DataTable';
import type {
  DataTableColumn,
  SortState,
} from '@components/shared/DataTable/DataTable';
import FilterPanel from '@components/shared/FilterPanel/FilterPanel';
import AppliedFiltersBar from '@components/shared/AppliedFiltersBar/AppliedFiltersBar';
import ProfileActionsMenu from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import type { ProfileAction } from '@components/shared/ProfileActionsMenu/ProfileActionsMenu';
import { getDocumentTypes, deleteDocumentType } from '@/services/documentTypeService';
import { useNotificationStore } from '@stores/notificationStore';
import { WorkpayApiError } from '@lib/apiEnvelope';
import { useAuthStore } from '@/stores/authStore';
import { useTableFilters } from '@/hooks/useTableFilters';
import type { DocumentTypeDto } from '@/types/documentType';
import type { FilterFieldConfig } from '@/types/filters';
import PlusIcon from '@assets/icons/PlusIcon';
import UsersEmptyIcon from '@assets/icons/UsersEmptyIcon';
import { validateTableSearch, buildSortString, toPaginationState } from '@utils/tabHelpers';
import { toTitleCase } from '@utils/textCase';
import type { TableSearchParams } from '@utils/tabHelpers';
import ViewDocumentTypeModal from '@features/document-types/ViewDocumentTypeModal';
import CreateEditDocumentTypeModal from '@features/document-types/CreateEditDocumentTypeModal';
import { AuditTrailModal } from '@components/shared/AuditTrailModal';
import { AUDIT_RESOURCE } from '@lib/auditResourceTypes';
import { useAuditTrigger } from '@/hooks/useAuditTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { Forbidden } from '@workpay/ds-shared/components/Forbidden/Forbidden';
import { isForbiddenError } from '@workpay/ds-shared/components/Forbidden/ForbiddenBoundary';
import styles from './index.module.scss';

export const Route = createFileRoute('/w/document-types/')({
  validateSearch: validateTableSearch,
  component: DocumentTypesPage,
});

// ============================================
// HELPERS
// ============================================

function StatusBadge({ status }: { status: DocumentTypeDto['status'] }) {
  const className = status === 'ACTIVE' ? styles.badgeActive : styles.badgeInactive;
  return <span className={`${styles.badge} ${className}`}>{toTitleCase(status)}</span>;
}

// ============================================
// COMPONENT
// ============================================

function DocumentTypesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addNotification = useNotificationStore((s) => s.addNotification);
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { page: urlPage, size: urlSize, sortBy, sortDir, filters: filtersParam } = Route.useSearch();

  const page = urlPage ?? 0;
  const size = urlSize ?? 10;

  const sort: SortState | undefined = useMemo(
    () => (sortBy && sortDir ? { columnId: sortBy, direction: sortDir } : undefined),
    [sortBy, sortDir],
  );

  // Filter fields ordered to match table columns:
  // Name, Identifier, Status, Created
  const filterFields = useMemo<FilterFieldConfig[]>(
    () => [
      {
        fieldKey: 'search',
        label: t('K_FILTER_NAME', 'Name'),
        type: 'text',
        placeholder: t('K_PLACEHOLDER_SEARCH_NAME', 'Search by name'),
      },
      {
        fieldKey: 'status',
        label: t('K_FILTER_STATUS', 'Status'),
        type: 'select',
        options: [
          { label: toTitleCase(t('K_STATUS_ACTIVE', 'Active')), value: 'ACTIVE' },
          { label: toTitleCase(t('K_STATUS_INACTIVE', 'Inactive')), value: 'INACTIVE' },
        ],
      },
    ],
    [t],
  );

  const {
    filters,
    hasFilters,
    activeFilterCount,
    isPanelOpen,
    togglePanel,
    closePanel,
    applyFilters,
    removeFilter,
    clearAllFilters,
    filterPayload,
  } = useTableFilters({
    routeTo: '/w/document-types',
    filtersParam,
    fields: filterFields,
  });

  // Modal state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewItem, setViewItem] = useState<DocumentTypeDto | null>(null);
  const [editItem, setEditItem] = useState<DocumentTypeDto | null>(null);
  const [deleteItem, setDeleteItem] = useState<DocumentTypeDto | null>(null);
  const audit = useAuditTrigger();
  const auditOpen = audit.open;

  const handleAuditFromView = useCallback(() => {
    if (viewItem) {
      audit.open(viewItem.id, viewItem.name || undefined);
      setViewItem(null);
    }
  }, [viewItem, audit]);

  const handleCreate = useCallback(() => setIsCreateOpen(true), []);
  const handleCloseCreate = useCallback(() => setIsCreateOpen(false), []);

  const handleView = useCallback((item: DocumentTypeDto) => setViewItem(item), []);
  const handleCloseView = useCallback(() => setViewItem(null), []);

  const handleEditFromView = useCallback(() => {
    if (viewItem) {
      setEditItem(viewItem);
      setViewItem(null);
    }
  }, [viewItem]);

  const handleDeleteFromView = useCallback(() => {
    if (viewItem) {
      setDeleteItem(viewItem);
      setViewItem(null);
    }
  }, [viewItem]);

  const handleCloseEdit = useCallback(() => setEditItem(null), []);

  const handleCloseDelete = useCallback(() => setDeleteItem(null), []);

  const deleteMutation = useMutation({
    mutationFn: deleteDocumentType,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['documentTypes'] });
      setDeleteItem(null);
    },
    onError: (error) => {
      const e = error as WorkpayApiError;
      addNotification({ variant: 'critical', title: t(e.description, e.description) });
    },
  });

  const handleConfirmDelete = useCallback(() => {
    if (deleteItem) {
      deleteMutation.mutate(deleteItem.id);
    }
  }, [deleteItem, deleteMutation]);

  const columns = useMemo<DataTableColumn<DocumentTypeDto>[]>(
    () => [
      {
        id: 'name',
        header: t('K_COLUMN_NAME', 'Name'),
        sortable: true,
        sortKey: 'name',
        width: '200px',
        cell: (item) => (
          <span className={styles.cellPrimary} title={item.name || '-'}>{item.name || '-'}</span>
        ),
      },
      {
        id: 'status',
        header: t('K_COLUMN_STATUS', 'Status'),
        sortable: true,
        sortKey: 'status',
        cell: (item) => <StatusBadge status={item.status} />,
      },
      {
        id: 'createdAt',
        header: t('K_COLUMN_CREATED', 'Created'),
        sortable: true,
        sortKey: 'createdAt',
        cell: (item) => item.createdAt ? new Date(item.createdAt).toLocaleDateString() : '-',
      },
    ],
    [t],
  );

  const rowActions = useCallback((item: DocumentTypeDto) => {
    const actions: ProfileAction[] = [];
    if (hasPermission('DOCUMENT_TYPES_UPDATE') && item.status !== 'INACTIVE') {
      actions.push({ id: 'edit', label: t('K_EDIT', 'Edit'), onClick: () => setEditItem(item) });
    }
    if (hasPermission('DOCUMENT_TYPES_DELETE') && item.status === 'ACTIVE') {
      actions.push({ id: 'deactivate', label: t('K_DEACTIVATE', 'Deactivate'), onClick: () => setDeleteItem(item) });
    }
    if (hasPermission('AUDIT_READ')) {
      actions.push({
        id: 'audit',
        label: t('K_AUDIT_TITLE', 'Audit trail'),
        onClick: () => auditOpen(item.id, item.name || undefined),
      });
    }
    if (actions.length === 0) return null;
    return <ProfileActionsMenu actions={actions} />;
  }, [t, hasPermission, auditOpen]);

  const sortString = useMemo(() => buildSortString(sort, columns), [sort, columns]);

  const handleSortChange = useCallback(
    (newSort: SortState | undefined) => {
      void navigate({
        to: '/w/document-types',
        search: (prev: TableSearchParams) => ({
          ...prev,
          page: 0,
          sortBy: newSort?.columnId,
          sortDir: newSort?.direction,
        }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      void navigate({
        to: '/w/document-types',
        search: (prev: TableSearchParams) => ({ ...prev, page: newPage }),
        replace: true,
      });
    },
    [navigate],
  );

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      void navigate({
        to: '/w/document-types',
        search: (prev: TableSearchParams) => ({ ...prev, size: newSize, page: 0 }),
        replace: true,
      });
    },
    [navigate],
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ['documentTypes', { page, size, sort: sortString, filterPayload }],
    queryFn: () => getDocumentTypes({ page, size, sort: sortString }, filterPayload),
  });

  const items = data?.content ?? [];
  const pagination = useMemo(() => toPaginationState(data), [data]);
  const listForbidden = isForbiddenError(error);

  const getRowKey = useCallback((item: DocumentTypeDto) => item.id, []);

  const handleRowClick = useCallback(
    (item: DocumentTypeDto) => handleView(item),
    [handleView],
  );

  const emptyState = useMemo(
    () =>
      listForbidden ? (
        <Forbidden variant="empty-state" hideAction />
      ) : (
        <div className={styles.emptyState}>
          <UsersEmptyIcon className={styles.emptyIcon} />
          <h2 className={styles.emptyTitle}>{t('K_EMPTY_NO_DOCUMENT_TYPES', 'No document types yet')}</h2>
          <p className={styles.emptyText}>
            {t('K_EMPTY_CREATE_FIRST_DOCUMENT_TYPE', 'Get started by creating your first document type.')}
          </p>
          {hasPermission('DOCUMENT_TYPES_CREATE') && (
            <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
              {t('K_BUTTON_CREATE_DOCUMENT_TYPE', 'Create document type')}
            </Button>
          )}
        </div>
      ),
    [listForbidden, handleCreate, t, hasPermission],
  );

  return (
    <div className={styles.page}>
      {/* Page Header */}
      <div className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{t('K_PAGE_TITLE_DOCUMENT_TYPES', 'Document Types')}</h1>
          <p className={styles.subtitle}>{t('K_PAGE_SUBTITLE_DOCUMENT_TYPES', 'Manage document type configurations')}</p>
        </div>

        {hasPermission('DOCUMENT_TYPES_CREATE') && (
          <Button variant="filled" size="lg" leftIcon={<PlusIcon />} onClick={handleCreate}>
            {t('K_BUTTON_CREATE_DOCUMENT_TYPE', 'Create document type')}
          </Button>
        )}
      </div>

      {hasFilters && (
        <AppliedFiltersBar
          fields={filterFields}
          values={filters}
          onRemove={removeFilter}
          onClearAll={clearAllFilters}
        />
      )}

      <DataTable
        columns={columns}
        data={items}
        rowKey={getRowKey}
        onRowClick={handleRowClick}
        loading={isLoading}
        loadingText={t('K_LOADING_DOCUMENT_TYPES', 'Loading document types...')}
        emptyState={emptyState}
        sort={sort}
        onSortChange={handleSortChange}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        showToolbar
        onFilterClick={togglePanel}
        activeFilterCount={activeFilterCount}
        rowActions={rowActions}
        aria-label="Document types list"
      />

      <FilterPanel
        isOpen={isPanelOpen}
        onClose={closePanel}
        fields={filterFields}
        values={filters}
        onApply={applyFilters}
        onReset={clearAllFilters}
        title={t('K_FILTER_TITLE', 'Filters')}
      />

      {/* View Modal */}
      <ViewDocumentTypeModal
        isOpen={viewItem !== null}
        onClose={handleCloseView}
        data={viewItem}
        onEdit={handleEditFromView}
        onDelete={handleDeleteFromView}
        onAudit={handleAuditFromView}
      />

      {/* Create Modal */}
      <CreateEditDocumentTypeModal
        isOpen={isCreateOpen}
        onClose={handleCloseCreate}
      />

      {/* Edit Modal */}
      <CreateEditDocumentTypeModal
        isOpen={editItem !== null}
        onClose={handleCloseEdit}
        editData={editItem}
      />

      <AuditTrailModal
        open={audit.target !== null}
        onClose={audit.close}
        filter={{ resourceType: AUDIT_RESOURCE.DOCUMENT_TYPE, resourceId: audit.target?.id ?? '' }}
        subject={audit.target?.name}
      />

      {/* Deactivate Confirmation */}
      <AlertModal
        variant="danger"
        title={t('K_MODAL_DEACTIVATE_DOCUMENT_TYPE', 'Deactivate Document Type')}
        description={t('K_CONFIRM_DEACTIVATE_DOCUMENT_TYPE', 'Are you sure you want to deactivate "{name}"? The document type will no longer be active.').replace('{name}', deleteItem?.name ?? '')}
        isOpen={deleteItem !== null}
        onClose={handleCloseDelete}
        primaryLabel={t('K_DEACTIVATE', 'Deactivate')}
        onPrimaryClick={handleConfirmDelete}
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
